import { useCallback } from "react";
import axios from "axios";
import {
    ActionPlace,
    CreateGroupRequest,
    DynamicsCompany,
    DynamicsOrder,
    IAction,
    IAssignedMembers,
    IChangeRequest,
    IFlow,
    IFlowExecution,
    IGraphUser,
    IGroup,
    ILog,
    IToken,
    IUnit,
    RequestFetchUnits,
    Settings,
    SpaceAcceptanceRequest
} from "../types/ApiTypes";
import { IWorkAtProject } from "../types/interfaces/IWorkAtProject";

const apiInstance = axios.create({
    baseURL: process.env["REACT_APP_API_URL"],
});

apiInstance.interceptors.request.use(config => {
    const token = sessionStorage.getItem('token');

    if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
    } else {
        delete apiInstance.defaults.headers.common.Authorization;
    }
    return config;
})

const useApi = () => {
    const fetchAppSettings = useCallback(async() => {
        const response = await apiInstance.get<Settings>('/settings');
        return response.data;
    },[])

    const changeAppSettings = useCallback(async(settings: Settings) => {
        const response = await apiInstance.put<Settings>('/settings',settings,);
        return response.data;
    },[])

    const fetchCompanyUnits = useCallback(async() => {
        const response = await apiInstance.get<RequestFetchUnits>('/unit');
        return response.data;
    },[])

    const fetchCompanyEmployees = useCallback(async() => {
        const response = await apiInstance.get<IGraphUser[]>('/user');
        return response.data;
    },[])

    const fetchUserGroups = useCallback(async() => {
        const response = await apiInstance.get<IGroup[]>('/space/user');
        return response.data;
    },[])

    const fetchGroups = useCallback(async(page: number = 1,sort: 'ASC'|'DESC' = 'ASC',all: boolean = false, query?: string, filter?: 'name'|'owner') => {
        const response = await apiInstance.get<{
            pages?: number,
            page?: number,
            count: number,
            spaces: IGroup[]
        }>('/space',{
            params: {
                all,
                sort,
                page,
                query,
                filter
            }
        });
        return response.data;
    },[])

    const fetchSpaceById = useCallback(async (id: string) =>
      (await apiInstance.get<IGroup>('/space/'+id)).data
    ,[]);

    const fetchActions = useCallback(async(place: ActionPlace) => {
        const response = await apiInstance.get<IAction[]>('/action/place/'+place);
        return response.data;
    },[]);

    const fetchCompanies = useCallback(async() => {
        const response = await apiInstance.get<DynamicsCompany[]>('/companies');
        return response.data || [];
    },[])

    const fetchProjects = useCallback(async() => {
        const response = await apiInstance.get<IWorkAtProject[]>('/projects');
        return response.data || [];
    },[])

    const fetchOrdersForCustomer = useCallback(async(customerId: string) => {
        const response = await apiInstance.get<DynamicsOrder[]>('/orders/customer/'+customerId);
        return response.data || [];
    },[])

    const fetchSiblingOrders = useCallback(async(orderId: string) => {
        const response = await apiInstance.get<DynamicsOrder[]>('/orders/sibling/'+orderId);
        return response.data || [];
    },[])

    const fetchSpaceLogs = useCallback(async(spaceId: string) => {
        const response = await apiInstance.get<ILog[]>('/log/space/'+spaceId);
        return response.data;
    },[])

    const fetchSpaceAssignedMembers = useCallback(async(spaceId: string) => {
        const response = await apiInstance.get<IAssignedMembers>('/space/'+spaceId+'/member');
        return response.data;
    },[])

    const fetchSystemLogs = useCallback(async(page: number = 1) => {
        const response = await apiInstance.get<ILog[]>('/log',{
            params: {
                page: page
            }
        });
        return response.data;
    },[])

    const fetchFlows = useCallback(async() => {
        const response = await apiInstance.get<{
            flows: IFlow[],
            flowsWithoutExecutionPolice: number
        }>('/flow');
        return response.data;
    },[])

    const fetchFlowExecutionPolicies = useCallback(async() => {
        const response = await apiInstance.get<IFlowExecution[]>('/flow/execution');
        return response.data;
    },[])

    const createGroupRequest = useCallback(async (data:CreateGroupRequest) => {
        const response = await apiInstance.post<IGroup>('/space',data);
        return response.data;
    },[])

    const rerunSpaceRequest = useCallback(async (spaceId: string) => {
        return (await apiInstance.patch<IGroup>(`space/${spaceId}/rerun`)).data;
    },[])

    const createFlow = useCallback(async ({url, name}:{url:string, name:string}) => {
        const response = await apiInstance.post<IFlow>('/flow', {url,name});
        return response.data;
    },[]);

    const createAction = useCallback(async(action: Omit<IAction,'id'|'flow'> & {flow: number}) => {
        const response = await apiInstance.post<IAction>('/action/',action);
        return response.data;
    },[]);

    const editAction = useCallback(async(action: Partial<Omit<IAction,'flow'|'id'>> & {flow: number, id:number}) => {
        const response = await apiInstance.put<IAction>('/action/'+action.id,action);
        return response.data;
    },[]);

    const triggerAction = useCallback(async(actionId: number, spaceId: string) => {
        const response = await apiInstance.post<IAction>('/action/trigger/'+actionId,{
            spaceId
        });
        return response.data;
    },[]);

    const createUnit = useCallback(async ({name,owner,parent_unit}:{name:string,owner:string,parent_unit?:number}) => {
        const response = await apiInstance.post<IUnit>('/unit', {name,owner,parent_unit});
        return response.data;
    },[]);

    const editUnit = useCallback(async (id: number, {name,owner,parent_unit}:{name:string,owner:string,parent_unit?:number}) => {
        const response = await apiInstance.patch<IUnit>('/unit/'+id, {name,owner,parent_unit});
        return response.data;
    },[]);

    const createFlowPolicy = useCallback(async ({flowId, order, groupType, actionType, extraPayload}:{flowId: number, order: number, groupType: number, actionType:string, extraPayload?: string}) => {
        const response = await apiInstance.post<IFlowExecution>('/flow/execution', {
            flowId,
            order,
            spaceType: groupType,
            actionType,
            extraPayload
        });
        return response.data;
    },[]);

    const editFlowPolicy = useCallback(async ({id, flowId, order, groupType, actionType, extraPayload}:{id: number, flowId: number, order: number, groupType: number, actionType:string, extraPayload?: string}) => {
        const response = await apiInstance.patch<IFlowExecution>('/flow/execution/'+id, {
            flowId,
            order,
            spaceType: groupType,
            actionType,
            extraPayload
        });
        return response.data;
    },[]);

    const updateFlow = useCallback(async (id:number, {newUrl, name}:{newUrl:string, name:string}) => {
        const response = await apiInstance.patch<IFlow>('/flow/'+id, {url:newUrl,name});
        return response.data;
    },[]);

    const updateSpaceRequest = useCallback(async (data: Partial<IGroup> & Pick<IGroup,'requestId'>) => {
        const response = await apiInstance.patch<IGroup>('/space/'+data.requestId, data);
        return response.data;
    },[]);

    const deleteFlowPolicy = useCallback(async (id:number) => {
        const response = await apiInstance.delete<IFlowExecution[]>('/flow/execution/'+id);
        return response.data;
    },[]);

    const deleteAction = useCallback(async (id:number) => {
        const response = await apiInstance.delete<undefined>('/action/'+id);
        return response.data;
    },[]);

    const deleteFlow = useCallback(async (id:number) => {
        const response = await apiInstance.delete<undefined>('/flow/'+id);
        return response.data;
    },[]);

    const deleteSpaceRequest = useCallback(async (id:string) => {
        const response = await apiInstance.delete<IGroup>('/space/'+id);
        return response.data;
    },[]);

    const deleteUnit = useCallback(async (id:number) => {
        const response = await apiInstance.delete<IUnit>('/unit/'+id);
        return response.data;
    },[]);

    const deleteMemberOrAssignment = useCallback(async (spaceId: string, memberId?: string, assignmentId?: string) => {
        await apiInstance.put('/space/'+spaceId+'/member',{
            membership: memberId,
            assignment: assignmentId
        });
        return true;
    },[]);

    const updateFlowPolicyOrder = useCallback(async (mappedObj:{[p:string]:number}) => {
        const response = await apiInstance.patch<IFlowExecution[]>('/flow/execution/order', mappedObj);
        return response.data;
    },[]);

    const toggleFlowPoliciesForAction = useCallback(async (action: IFlowExecution['action'], spaceType: number, isDisabled: boolean) => {
        const response = await apiInstance.patch<IFlowExecution[]>(`/flow/execution/toggle/${action}`, {
            isDisabled,
            spaceType
        });
        return response.data;
    },[]);

    const toggleFlowPolicy = useCallback(async (id: number, isDisabled: boolean) => {
        const response = await apiInstance.patch<IFlowExecution>(`/flow/execution/${id}/toggle`, {
            isDisabled
        });
        return response.data;
    },[]);

    const changeSpaceAcceptance = useCallback(async (data:SpaceAcceptanceRequest, id:string) => {
        const response = await apiInstance.patch<IGroup>(`/space/${id}/acceptance`,data);
        return response.data;
    },[])

    const fetchChangeRequestsForSpace = useCallback(async (spaceId: string) => {
        const response = await apiInstance.get<IChangeRequest[]>(`/changeRequest/space/${spaceId}`);
        return response.data;
    },[]);
    
    const createChangeRequest = useCallback(async (data: Pick<IChangeRequest, 'reason'|'new_owner'|'type'> & {spaceId: string}) => {
        const response = await apiInstance.post<IChangeRequest>(`/changeRequest`,data);
        return response.data;
    },[]);

    const getSpaceHeartbeat = useCallback(async (spaceId: string) => {
        const response = await apiInstance.get<{
            aadGroup: boolean,
            aadAccessPackage: boolean,
            aadMemberPolicy: boolean,
            aadOwnerPolicy: boolean,
            aadGuestPolicy: boolean
        }>(`/space/${spaceId}/heartbeat`);
        return response.data;
    },[]);
    
    const getApiTokens = useCallback(async () => {
        const response = await apiInstance.get<IToken[]>('/settings/token');
        return response.data;
    },[])

    const createApiToken = useCallback(async (name: string) => {
        const response = await apiInstance.post<IToken>('/settings/token',{
            name
        });
        return response.data;
    },[])

    return {
        fetchAppSettings,
        changeAppSettings,
        fetchCompanyUnits,
        fetchSpaceById,
        fetchCompanyEmployees,
        fetchUserGroups,
        fetchGroups,
        fetchCompanies,
        fetchProjects,
        fetchSpaceLogs,
        fetchSystemLogs,
        fetchSpaceAssignedMembers,
        fetchFlows,
        fetchFlowExecutionPolicies,
        fetchOrdersForCustomer,
        fetchSiblingOrders,
        fetchActions,
        fetchChangeRequestsForSpace,
        createGroupRequest,
        createFlow,
        createFlowPolicy,
        createUnit,
        createAction,
        editAction,
        editFlowPolicy,
        editUnit,
        createChangeRequest,
        updateFlow,
        updateFlowPolicyOrder,
        toggleFlowPoliciesForAction,
        toggleFlowPolicy,
        updateSpaceRequest,
        deleteFlowPolicy,
        deleteSpaceRequest,
        deleteMemberOrAssignment,
        deleteUnit,
        deleteAction,
        deleteFlow,
        changeSpaceAcceptance,
        triggerAction,
        getSpaceHeartbeat,
        getApiTokens,
        createApiToken,
        rerunSpaceRequest
    }
}

export default useApi;